import React from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck,
  BookOpenCheck,
  Stethoscope,
  BadgeCheck,
  ArrowRight,
  Lock,
  Users,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1659353888906-adb3e0041693?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxoaXNwYW5pYyUyMGRvY3RvciUyMHBvcnRyYWl0fGVufDB8fHx8MTc4MjU0MTk3NXww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-grid pointer-events-none" aria-hidden />
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-14 grid lg:grid-cols-2 gap-12 items-center relative">
          <div className="rise-in">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-xs font-medium text-[#0F2942] mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Orientación clínica IA basada en GPC de México
            </div>
            <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#0F2942] leading-[1.05]">
              Salud confiable, <br />
              <span className="text-[#2563EB]">respaldada por guías</span> <br />
              del sector salud.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-lg leading-relaxed">
              Consulta con nuestro asistente clínico IA por <b className="text-foreground">$39 MXN</b> y,
              cuando lo necesites, deriva a un especialista de nuestro Directorio Médico. Sin
              recetas encubiertas. Sin diagnósticos irresponsables. Solo orientación seria basada en las
              Guías de Práctica Clínica del IMSS y la SSA.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/registro">
                <Button
                  size="lg"
                  className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white text-base px-6 h-12 rounded-lg"
                  data-testid="hero-register-btn"
                >
                  Crear cuenta gratis <ArrowRight size={16} />
                </Button>
              </Link>
              <Link to="/directorio">
                <Button
                  size="lg"
                  variant="outline"
                  className="text-base px-6 h-12 rounded-lg border-border"
                  data-testid="hero-directorio-btn"
                >
                  <Users size={16} /> Ver directorio médico
                </Button>
              </Link>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md text-xs text-muted-foreground">
              <TrustPill icon={<Lock size={12} />} label="Datos cifrados AES-256" />
              <TrustPill icon={<BookOpenCheck size={12} />} label="GPC IMSS / SSA" />
              <TrustPill icon={<ShieldCheck size={12} />} label="LFPDPPP" />
            </div>
          </div>

          <div className="rise-in rise-delay-2">
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-[#2563EB]/8 via-transparent to-transparent rounded-3xl pointer-events-none" />
              <div className="relative rounded-2xl overflow-hidden border border-border bg-white shadow-sm">
                <img
                  src={HERO_IMG}
                  alt="Profesional médica en consulta"
                  className="w-full h-[440px] object-cover"
                />
                <div className="p-5 border-t border-border">
                  <div className="flex items-center gap-3">
                    <BadgeCheck className="text-[#2563EB]" size={20} />
                    <div>
                      <div className="text-sm font-semibold text-[#0F2942]">
                        Solo OTC · COFEPRIS Fracción V y VI
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Cruce automático de alergias, contraindicaciones e interacciones
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING / VALUE BENTO */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 rounded-xl border border-border bg-white p-8">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-[#2563EB]">
              <Stethoscope size={14} /> Para personas usuarias
            </div>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-[#0F2942]">
              Consulta IA por <span className="text-[#2563EB]">$39 MXN</span>
            </h2>
            <p className="mt-2 text-sm text-muted-foreground max-w-lg">
              Describe tu cuadro, tu historia clínica y tus laboratorios. Nuestro motor cruza tu perfil
              contra los medicamentos OTC permitidos por COFEPRIS y las guías clínicas mexicanas. Si el
              caso no es leve, autolimitado y seguro, te deriva al Directorio de Especialistas.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-2 text-sm text-foreground">
              <ValueBullet>Sin recetas encubiertas: solo OTC (V/VI)</ValueBullet>
              <ValueBullet>Cita explícita a la GPC correspondiente</ValueBullet>
              <ValueBullet>Alerta automática de banderas rojas</ValueBullet>
              <ValueBullet>Repositorio cifrado con AES-256-GCM</ValueBullet>
            </ul>
          </div>

          <div className="rounded-xl border border-border bg-[#0F2942] text-white p-8 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-white/60">
                <Users size={14} /> Para médicos
              </div>
              <h2 className="mt-3 font-display text-2xl font-semibold">
                Directorio con suscripción
              </h2>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">
                Recibe referidos calificados. Perfil verificado, ficha profesional y visibilidad en la app.
              </p>
            </div>
            <div className="mt-6 space-y-2 text-sm">
              <div className="flex items-baseline justify-between border-b border-white/10 pb-2">
                <span>Mensual</span>
                <span className="font-semibold">$69 MXN / mes</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span>Anual</span>
                <span className="font-semibold">
                  $699 <span className="text-white/60 text-xs">/ año</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPLIANCE STRIP */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="rounded-xl border border-border bg-white p-6 sm:p-8 grid md:grid-cols-4 gap-6">
          <ComplianceItem
            code="NOM-004-SSA3-2012"
            label="Expediente clínico"
            copy="Estructura de datos y trazabilidad del expediente electrónico."
          />
          <ComplianceItem
            code="LFPDPPP"
            label="Datos personales"
            copy="Aviso de privacidad, consentimiento explícito y derechos ARCO."
          />
          <ComplianceItem
            code="COFEPRIS"
            label="Fracción V y VI"
            copy="Solo medicamentos de venta libre autorizados. Nunca receta médica."
          />
          <ComplianceItem
            code="GPC IMSS/SSA"
            label="Práctica clínica"
            copy="Toda recomendación cita la Guía de Práctica Clínica correspondiente."
          />
        </div>
      </section>

      <Footer />
    </div>
  );
}

function TrustPill({ icon, label }) {
  return (
    <div className="flex items-center gap-1.5 rounded-full border border-border bg-white px-2.5 py-1.5">
      <span className="text-[#2563EB]">{icon}</span>
      <span className="text-[11px] text-foreground font-medium">{label}</span>
    </div>
  );
}

function ValueBullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#2563EB] shrink-0" />
      <span className="text-sm text-foreground">{children}</span>
    </li>
  );
}

function ComplianceItem({ code, label, copy }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-display font-semibold text-[#0F2942] mt-1">{code}</div>
      <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{copy}</p>
    </div>
  );
}
